// AXIS ZERO - Nickname Input Modal
// Shows when player achieves a new high score

import React, { useState, useEffect, useRef } from 'react';
import { Trophy, X } from 'lucide-react';
import { audioManager } from '../../game';

export const NicknameModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  score, 
  defaultNickname = '' 
}) => {
  const [nickname, setNickname] = useState(defaultNickname);
  const inputRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [isOpen]);

  // Reset nickname when modal opens
  useEffect(() => {
    if (isOpen) {
      setNickname(defaultNickname);
    }
  }, [isOpen, defaultNickname]);

  if (!isOpen) return null;

  const handleSave = () => {
    audioManager.playButtonClick();
    onSave(nickname.trim() || 'PLAYER');
  };

  const handleSkip = () => {
    audioManager.playButtonClick();
    onSave(defaultNickname || 'PLAYER');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleSkip();
    }
  };

  const formatScore = (s) => {
    return typeof s === 'number' ? s.toFixed(2) : s;
  };

  return (
    <div 
      data-testid="nickname-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(5, 5, 5, 0.98)' }}
    >
      <div 
        className="w-full max-w-sm p-6 rounded-lg"
        style={{
          backgroundColor: 'rgba(20, 20, 20, 0.95)',
          border: '2px solid #39FF14',
          boxShadow: '0 0 40px rgba(57, 255, 20, 0.3)',
        }}
      >
        {/* Trophy Icon */}
        <div className="flex justify-center mb-4">
          <div 
            className="p-4 rounded-full"
            style={{ 
              backgroundColor: 'rgba(57, 255, 20, 0.2)',
              border: '2px solid #39FF14',
            }}
          >
            <Trophy size={40} style={{ color: '#FFD700' }} />
          </div>
        </div>

        {/* Title */}
        <h2 
          className="text-2xl font-black text-center uppercase tracking-wider mb-2"
          style={{ 
            color: '#39FF14',
            fontFamily: '"Orbitron", sans-serif',
            textShadow: '0 0 20px rgba(57, 255, 20, 0.8)',
          }}
        >
          NEW HIGH SCORE!
        </h2>

        {/* Score Display */}
        <div 
          className="text-3xl font-bold text-center mb-6"
          style={{ 
            color: '#00F0FF',
            fontFamily: '"Orbitron", sans-serif',
            textShadow: '0 0 15px rgba(0, 240, 255, 0.6)',
          }}
        >
          {formatScore(score)}s
        </div>

        {/* Nickname Input */}
        <div className="mb-6">
          <label 
            className="block text-sm uppercase tracking-wider mb-2"
            style={{ color: 'rgba(255, 255, 255, 0.6)' }}
          >
            Enter your name
          </label>
          <input
            ref={inputRef}
            data-testid="nickname-input"
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value.slice(0, 12))}
            onKeyDown={handleKeyDown}
            placeholder="PLAYER"
            maxLength={12}
            className="w-full px-4 py-3 rounded text-center text-lg font-bold uppercase tracking-wider outline-none"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              border: '2px solid rgba(0, 240, 255, 0.5)',
              color: '#00F0FF',
              fontFamily: '"Orbitron", sans-serif',
            }}
          />
          <div 
            className="text-xs text-right mt-1"
            style={{ color: 'rgba(255, 255, 255, 0.3)' }}
          >
            {nickname.length}/12
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            data-testid="nickname-skip-btn"
            onClick={handleSkip}
            className="flex-1 py-3 border-2 font-bold uppercase tracking-wider rounded transition-all"
            style={{ 
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            Skip
          </button>
          <button
            data-testid="nickname-save-btn"
            onClick={handleSave}
            className="flex-1 py-3 border-2 font-bold uppercase tracking-wider rounded transition-all"
            style={{ 
              borderColor: '#39FF14',
              color: '#39FF14',
              backgroundColor: 'rgba(57, 255, 20, 0.1)',
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default NicknameModal;
